import { useRef, useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import useFetchProduct from "./useFetchProduct";
import { compressImage } from "./utils/compressImage";
import { API } from "../../../config";
import { useAuth } from "../../../utils/AuthContext";
function useProduct(editingProduct = null, onSuccess = null) {
  const { user } = useAuth();
  const [mainImagePreview, setMainImagePreview] = useState("");
  const [selectedImages, setSelectedImages] = useState([]);
  const [err, setErr] = useState("");
  const formDataRef = useRef(new FormData());
  const [values, setValues] = useState({
    name: "",
    description: "",
    price: "",
    category: "",
    categories: "",
    quantity: "",
    photo: "",
    photos: "",
    shipping: "",
    error: "",
    sold: 0,
    loading: false,
    createdProduct: "",
    redirectToProfile: "",
  });
  const {
    createProductFn,
    updateProductFn,
    isUpdatingProduct,
    isCreatingProduct,
  } = useFetchProduct(
    {},
    {
      onCreateSuccess: onSuccess,
      onUpdateSuccess: onSuccess,
    }
  );

  // Populate form when editingProduct is provided
  useEffect(() => {
    if (editingProduct) {
      // console.log("Editing product:", editingProduct);
      setValues({
        name: editingProduct.name || "",
        description: editingProduct.description || "",
        price: editingProduct.price || "",
        category: editingProduct.category?._id || editingProduct.category || "",
        quantity: editingProduct.quantity || editingProduct.Quentity || "",
        shipping: editingProduct.shipping?.toString() || "",
        sold: editingProduct.sold || 0,
        photo: editingProduct.photo,
        photos: editingProduct.photos,
        error: "",
        loading: false,
        createdProduct: "",
        redirectToProfile: "",
      });

      // Set main image preview for editing product (always available)
      const mainImageUrl = `${API}/products/photo/${editingProduct._id}`;
      // console.log("Setting main image preview:", mainImageUrl);
      setMainImagePreview(mainImageUrl);

      // Set additional images if they exist (excluding the main image)
      // console.log("editingProduct.photos:", editingProduct.photos);
      if (editingProduct.photos && editingProduct.photos.length > 0) {
        // Check if photos are already URLs or need to be converted
        const photoUrls = editingProduct.photos.map((photo) => {
          // Convert to API URL
          return `${API}/${photo}`;
        });
        setSelectedImages(photoUrls);
      } else {
        setSelectedImages([]);
      }
    }
  }, [editingProduct]);

  // Clear previews when switching from edit to create mode
  useEffect(() => {
    if (!editingProduct) {
      setMainImagePreview("");
      setSelectedImages([]);
    }
  }, [editingProduct]);

  const onChangeHandler = (name) => async (event) => {
    if (name === "photo") {
      const file = event.target.files[0];

      try {
        const resizedImage = await compressImage(file);
        const preview = URL.createObjectURL(file);
        setMainImagePreview(preview);
        formDataRef.current.set(name, resizedImage);
        setValues({ ...values, [name]: resizedImage });
      } catch (error) {
        console.error("Error resizing image:", error);
        // Fallback to original file if resize fails
        const preview = URL.createObjectURL(file);
        setMainImagePreview(preview);
        formDataRef.current.set(name, file);
        setValues({ ...values, [name]: file });
      }
    } else if (name === "photos") {
      // Multiple images upload
      const files = Array.from(event.target.files);
      if (selectedImages.length + files.length > 4) {
        setErr(true);
        return;
      }

      setErr(false);
      // try {
      //   const resizedFiles = await Promise.all(
      //     files.map((file) => compressImage(file))
      //   );
      //   const previews = resizedFiles.map((file) => URL.createObjectURL(file));
      //   setSelectedImages((prev) => [...prev, ...previews]);

      //   resizedFiles.forEach((file) => formDataRef.current.set(name, file));
      //   setValues((prev) => ({
      //     ...prev,
      //     photos: [...(prev.photos || []), ...resizedFiles],
      //   }));
      // } catch (error) {
      //   console.error("Error resizing multiple images:", error);
      //   // Fallback to original files if resize fails
      //   const previews = files.map((file) => URL.createObjectURL(file));
      //   setSelectedImages((prev) => [...prev, ...previews]);
      //   files.forEach((file) => formDataRef.current.append(name, file));
      //   setValues((prev) => ({
      //     ...prev,
      //     photos: [...(prev.photos || []), ...files],
      //   }));
      // }
    } else {
      formDataRef.current.set(name, event.target.value);
      setValues({ ...values, [name]: event.target.value });
    }
  };
  const removeMainImage = () => {
    setMainImagePreview("");
    formDataRef.current.delete("photo");
    setValues((prevValues) => ({
      ...prevValues,
      photo: null,
    }));
  };
  const removeImage = (index) => {
    setSelectedImages((prevPreviews) => {
      const updatedPreviews = prevPreviews.filter((_, i) => i !== index);

      // Remove corresponding image from values.photos
      setValues((prevValues) => {
        const updatedPhotos =
          prevValues.photos?.filter((_, i) => i !== index) || [];

        // Rebuild FormData photos field
        const newFormData = new FormData();
        formDataRef.current = newFormData;

        // Keep all other fields (if needed)
        for (let [key, value] of formDataRef.current.entries()) {
          if (key !== "photos") {
            newFormData.append(key, value);
          }
        }

        // Add remaining photos
        updatedPhotos.forEach((file) => newFormData.append("photos", file));
        formDataRef.current = newFormData;
        // update your outer formData reference

        return { ...prevValues, photos: updatedPhotos };
      });

      if (updatedPreviews.length <= 4) {
        setErr(false);
      }

      return updatedPreviews;
    });
  };
  const onClickHandler = async (e) => {
    e.preventDefault();
    setValues((prevValues) => ({ ...prevValues, error: "" }));

    if (
      !editingProduct &&
      (!values.name ||
        !values.description ||
        !values.price ||
        !values.category ||
        !values.shipping ||
        !values.quantity)
    ) {
      setValues((prevValues) => ({
        ...prevValues,
        error: "All fields are required!",
      }));
      toast.error("please provide all fields");
      return;
    }

    try {
      // Prepare form data
      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("description", values.description);
      formData.append("price", values.price);
      formData.append("category", values.category);
      formData.append("shipping", values.shipping);
      formData.append("quantity", values.quantity);
      formData.append("sold", values.sold);

      // Handle main image
      // if (values.photo && typeof values.photo === "object") {
      //   const MAX_SIZE = 100 * 1024;
      //   const compressedFile = await compressImage(values.photo, 0.6);
      //   if (compressedFile.size > MAX_SIZE) {
      //     toast.error(
      //       "Compressed image is still too large! Please upload a smaller file."
      //     );
      //     return;
      //   }
      //   formData.append("photo", compressedFile);
      // }

      // Handle additional images
      if (values.photos && Array.isArray(values.photos)) {
        values.photos.forEach((photo) => {
          if (typeof photo === "object") {
            formData.append("photos", photo);
          }
        });
      }

      if (editingProduct) {
        // Update existing product
        updateProductFn({
          productId: editingProduct._id,
          productData: formData,
        });
      } else {
        // Create new product
        if (!values.photo || typeof values.photo !== "object") {
          toast.error("Please select an image.");
          return;
        }
        // For creation, pass the values object directly (not FormData)
        createProductFn({
          ...values,
          photo: values.photo,
          photos: values.photos,
        });
      }

      // Reset form after successful operation
      setValues((prevValues) => ({
        ...prevValues,
        name: "",
        description: "",
        price: "",
        category: "",
        categories: "",
        quantity: "",
        photo: "",
        photos: "",
        shipping: "",
        error: "",
        sold: 0,
        createdProduct: "",
        redirectToProfile: "",
        formData: "",
      }));
      setMainImagePreview("");
      setSelectedImages([]);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Operation failed! Please try again.");
    }
  };

  return {
    mainImagePreview,
    selectedImages,
    err,
    formDataRef,
    values,
    onChangeHandler,
    removeMainImage,
    removeImage,
    onClickHandler,
    isUpdatingProduct,
    isCreatingProduct,
  };
}

export default useProduct;
